import { useCallback, useMemo } from 'react';
import type { LoomVideo } from '@loomhq/record-sdk';

import { useEditPageLoadingActions } from '@confluence/editor-loading-context';
import { useLoomInsertUrlActions } from '@confluence/loom-insert-url-store';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { getUrlForContentType } from '@confluence/content-types-utils/entry-points/getUrlForContentType';

export const CONTENT_TYPE = 'page';
export const SOURCE_COMPONENT = 'readTimeDropdownLoomRecording';

/**
 * @param contentId of the page that the recorder should be opened on
 * @param flags need to be passed because this does not return a `ReactNode`
 * @returns a function that opens the recorder allowing insertion of video into page with {@link contentId}
 */
export const useHandleOpenLoomRecorder = (contentId: string, flags: FlagsStateContainer) => {
	const { loadEditor } = useEditPageLoadingActions();
	const loomInsertUrlActions = useLoomInsertUrlActions();
	const spaceKey = useSpaceKey() || '';

	const redirectUrl =
		getUrlForContentType({
			contentType: CONTENT_TYPE,
			spaceKey,
			contentId,
			redirectToEditor: true,
		}) || '';

	const saveLoomUrlAndLoadEditor = useCallback(
		(video: LoomVideo) => {
			loomInsertUrlActions.updateFullPageEditorLoomUrl(video);

			loadEditor &&
				loadEditor({
					contentId,
					spaceKey,
					redirectUrl,
					contentType: CONTENT_TYPE,
				});
		},
		[loomInsertUrlActions, contentId, spaceKey, loadEditor, redirectUrl],
	);

	const openLoomRecorderParams = useMemo(
		() => ({
			onInsert: (_: unknown, video: LoomVideo) => {
				saveLoomUrlAndLoadEditor(video);
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert Loom video',
		}),
		[saveLoomUrlAndLoadEditor],
	);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		entryPointLocation: SOURCE_COMPONENT,
		flags,
		resetInsertFunctionOnUnmount: false,
	});

	const handleOpenLoomRecorder = useCallback(() => {
		void openLoomRecorder(openLoomRecorderParams);
	}, [openLoomRecorder, openLoomRecorderParams]);

	return handleOpenLoomRecorder;
};
